import { useState, useEffect, useMemo } from "react";
import { useProfile } from "@/hooks/useProfile";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { Clock } from "lucide-react";

function resolveTimezone(profileTz: string | null | undefined): string {
  if (!profileTz || profileTz === "auto" || profileTz === "UTC") {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  return profileTz;
}

export const GlobalClock = () => {
  const { data: profile } = useProfile();
  const { isRTL } = useLanguage();
  const isMobile = useIsMobile();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const tz = useMemo(() => resolveTimezone(profile?.timezone), [profile?.timezone]);

  const localTime = now.toLocaleTimeString("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const utcTime = now.toLocaleTimeString("en-GB", {
    timeZone: "UTC",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const tzLabel = tz.replace(/_/g, " ");

  if (isMobile) {
    return (
      <div className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground">
        <Clock className="w-3 h-3" />
        <span className="text-foreground font-medium">{localTime}</span>
        <span className="text-muted-foreground">UTC {utcTime}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start text-xs font-mono leading-tight">
      <div className="flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5 text-primary" />
        <span className="text-foreground font-semibold text-sm">{localTime}</span>
      </div>
      <span className="text-muted-foreground text-[10px] ms-[18px]">{tzLabel}</span>
      <span className="text-muted-foreground text-[10px] ms-[18px]">UTC {utcTime}</span>
    </div>
  );
};
